import { apiContractRequest } from "./client";

const jsonPost = (body) => ({ method: "POST", body });
const jsonPatch = (body) => ({ method: "PATCH", body });

function buildOwnerImportFormData(file) {
  const formData = new FormData();
  formData.append("sourceType", "upload");
  formData.append("file", file);
  return formData;
}


export const ownerApi = {
  overview() {
    return apiContractRequest("/api/owner/overview");
  },

  center() {
    return apiContractRequest("/api/owner/center");
  },
  assistant({ month, currency = "ARS", tzOffsetMinutes = new Date().getTimezoneOffset() } = {}) {
    return apiContractRequest("/api/owner/assistant", { params: { month, currency, tzOffsetMinutes } });
  },
  calendar({ month, date, tzOffsetMinutes = new Date().getTimezoneOffset() } = {}) {
    return apiContractRequest("/api/owner/calendar", { params: { month, date, tzOffsetMinutes } });
  },
  updateCalendarSettings(payload) {
    return apiContractRequest("/api/owner/calendar/settings", jsonPatch(payload));
  },
  createRoutine(payload) {
    return apiContractRequest("/api/owner/calendar/routines", jsonPost(payload));
  },
  updateRoutine(routineId, payload) {
    return apiContractRequest(`/api/owner/calendar/routines/${routineId}`, jsonPatch(payload));
  },
  listLifeEntries({ status = "inbox", limit = 40 } = {}) {
    return apiContractRequest("/api/owner/life/entries", { params: { status, limit } });
  },
  createLifeEntry(payload) {
    return apiContractRequest("/api/owner/life/entries", jsonPost(payload));
  },
  updateLifeEntry(entryId, payload) {
    return apiContractRequest(`/api/owner/life/entries/${entryId}`, jsonPatch(payload));
  },
  listCommitments({ status = "open", limit = 80 } = {}) {
    return apiContractRequest("/api/owner/life/commitments", { params: { status, limit } });
  },
  createCommitment(payload) {
    return apiContractRequest("/api/owner/life/commitments", jsonPost(payload));
  },
  updateCommitment(commitmentId, payload) {
    return apiContractRequest(`/api/owner/life/commitments/${commitmentId}`, jsonPatch(payload));
  },
  listCommitmentTime(commitmentId) {
    return apiContractRequest(`/api/owner/life/commitments/${commitmentId}/time`);
  },
  addCommitmentTime(commitmentId, payload) {
    return apiContractRequest(`/api/owner/life/commitments/${commitmentId}/time`, jsonPost(payload));
  },
  listInitiatives({ status = "active", limit = 40 } = {}) {
    return apiContractRequest("/api/owner/life/initiatives", { params: { status, limit } });
  },
  createInitiative(payload) {
    return apiContractRequest("/api/owner/life/initiatives", jsonPost(payload));
  },
  updateInitiative(initiativeId, payload) {
    return apiContractRequest(`/api/owner/life/initiatives/${initiativeId}`, jsonPatch(payload));
  },

  listFinanceAccounts() {
    return apiContractRequest("/api/owner/finance/accounts");
  },
  createFinanceAccount(payload) {
    return apiContractRequest("/api/owner/finance/accounts", jsonPost(payload));
  },
  listFinanceTransactions({ limit = 80 } = {}) {
    return apiContractRequest("/api/owner/finance/transactions", {
      params: { limit },
    });
  },
  createFinanceTransaction(payload) {
    return apiContractRequest("/api/owner/finance/transactions", jsonPost(payload));
  },
  listRecurringFinance({ includeEnded = false } = {}) {
    return apiContractRequest("/api/owner/finance/recurring", { params: { includeEnded } });
  },
  createRecurringFinance(payload) {
    return apiContractRequest("/api/owner/finance/recurring", jsonPost(payload));
  },
  updateRecurringFinance(planId, payload) {
    return apiContractRequest(`/api/owner/finance/recurring/${planId}`, jsonPatch(payload));
  },
  recordRecurringFinance(planId, payload) {
    return apiContractRequest(`/api/owner/finance/recurring/${planId}/record`, jsonPost(payload));
  },
  linkFinanceTransactionRecurring(transactionId, payload) {
    return apiContractRequest(`/api/owner/finance/transactions/${transactionId}/recurring`, jsonPost(payload));
  },
  createFinanceImport(file) {
    return apiContractRequest("/api/owner/finance/imports", {
      method: "POST",
      body: buildOwnerImportFormData(file),
    });
  },
  previewFinanceImport(importId) {
    return apiContractRequest(`/api/owner/finance/imports/${encodeURIComponent(importId)}/preview`, jsonPost({}));
  },
  commitFinanceImport(importId) {
    return apiContractRequest(`/api/owner/finance/imports/${encodeURIComponent(importId)}/commit`, jsonPost({}));
  },

  commercialSummary() {
    return apiContractRequest("/api/owner/commercial/summary");
  },
  listClients({ status = "", query = "", limit = 250 } = {}) {
    return apiContractRequest("/api/owner/commercial/clients", { params: { ...(status ? { status } : {}), ...(query ? { query } : {}), limit } });
  },
  createClient(payload) {
    return apiContractRequest("/api/owner/commercial/clients", jsonPost(payload));
  },
  updateClient(clientId, payload) {
    return apiContractRequest(`/api/owner/commercial/clients/${clientId}`, jsonPatch(payload));
  },
  searchWebProfitUsers({ query = "", limit = 20 } = {}) {
    return apiContractRequest("/api/owner/webprofit/users", { params: { query, limit } });
  },
  searchWebProfitProjects({ query = "", limit = 20 } = {}) {
    return apiContractRequest("/api/owner/webprofit/projects", { params: { query, limit } });
  },
  clientWebProfitContext(clientId) {
    return apiContractRequest(`/api/owner/people/${clientId}/webprofit`);
  },
  linkClientWebProfitUser(clientId, userId) {
    return apiContractRequest(`/api/owner/people/${clientId}/webprofit`, jsonPatch({ userId: userId || null }));
  },
  linkClientWebProfitProject(clientId, projectId, linked = true) {
    return apiContractRequest(`/api/owner/people/${clientId}/webprofit/projects/${projectId}`, jsonPatch({ linked }));
  },
  peopleFocus({ limit = 20 } = {}) {
    return apiContractRequest("/api/owner/people/focus", { params: { limit } });
  },
  personContext(clientId) {
    return apiContractRequest(`/api/owner/people/${clientId}/context`);
  },
  clientChatGptExport(clientId) {
    return apiContractRequest(`/api/owner/people/${clientId}/chatgpt-export`);
  },
  listInteractions(clientId, { limit = 60 } = {}) {
    return apiContractRequest(`/api/owner/people/${clientId}/interactions`, { params: { limit } });
  },
  createInteraction(clientId, payload) {
    return apiContractRequest(`/api/owner/people/${clientId}/interactions`, jsonPost(payload));
  },
  listServices({ includeInactive = true } = {}) {
    return apiContractRequest("/api/owner/commercial/services", { params: { includeInactive } });
  },
  createService(payload) {
    return apiContractRequest("/api/owner/commercial/services", jsonPost(payload));
  },
  updateService(serviceId, payload) {
    return apiContractRequest(`/api/owner/commercial/services/${serviceId}`, jsonPatch(payload));
  },
  listSales({ limit = 120, status = "" } = {}) {
    return apiContractRequest("/api/owner/commercial/sales", {
      params: { limit, ...(status ? { status } : {}) },
    });
  },
  createSale(payload) {
    return apiContractRequest("/api/owner/commercial/sales", jsonPost(payload));
  },
  updateSaleStatus(saleId, status) {
    return apiContractRequest(`/api/owner/commercial/sales/${saleId}/status`, jsonPatch({ status }));
  },
  listSalePayments(saleId) {
    return apiContractRequest(`/api/owner/commercial/sales/${saleId}/payments`);
  },
  createSalePayment(saleId, payload) {
    return apiContractRequest(`/api/owner/commercial/sales/${saleId}/payments`, jsonPost(payload));
  },
};
